import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';

const TEXT = {
  es: {
    title: 'Comisiones por canal',
    description: 'Lo que te cobra cada plataforma o pasarela de pago por venta. Se aplica a los pedidos nuevos que lleguen de cada integraci\u00f3n. Consulta tus tarifas reales en el panel de cada plataforma.',
    percent: 'Porcentaje (%)',
    fixed: 'Fijo por pedido (\u20ac)',
    save: 'Guardar',
    saving: 'Guardando...',
    saved: 'Guardado. Se aplicar\u00e1 a los pedidos nuevos.',
    error: 'No se pudo guardar. Revisa que los valores sean n\u00fameros entre 0 y 100.',
    loading: 'Cargando...',
  },
  en: {
    title: 'Fees by channel',
    description: 'What each platform or payment gateway charges you per sale. Applied to new orders coming from each integration. Check your real rates in each platform\u2019s dashboard.',
    percent: 'Percentage (%)',
    fixed: 'Fixed per order (\u20ac)',
    save: 'Save',
    saving: 'Saving...',
    saved: 'Saved. It will apply to new orders.',
    error: 'Could not save. Check that the values are numbers between 0 and 100.',
    loading: 'Loading...',
  },
  fr: {
    title: 'Commissions par canal',
    description: 'Ce que chaque plateforme ou passerelle de paiement vous facture par vente. S\u2019applique aux nouvelles commandes de chaque int\u00e9gration. Consultez vos tarifs r\u00e9els dans le tableau de bord de chaque plateforme.',
    percent: 'Pourcentage (%)',
    fixed: 'Fixe par commande (\u20ac)',
    save: 'Enregistrer',
    saving: 'Enregistrement...',
    saved: 'Enregistr\u00e9. S\u2019appliquera aux nouvelles commandes.',
    error: 'Impossible d\u2019enregistrer. V\u00e9rifiez que les valeurs sont des nombres entre 0 et 100.',
    loading: 'Chargement...',
  },
};

const CHANNELS = [
  ['shopify', 'Shopify'],
  ['woocommerce', 'WooCommerce'],
  ['tiktok', 'TikTok Shop'],
];

const getText = (lang) => TEXT[(lang || 'es').slice(0, 2)] || TEXT.es;
const parseNumber = (value) => Number(String(value).trim().replace(',', '.'));

function FeeSettingsCard({ token, onAuthError, currency }) {
  const { i18n } = useTranslation();
  const tx = getText(i18n.language);
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const headers = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios
      .get(`${API_URL}/api/v1/tenant/fees`, headers)
      .then((response) => {
        const data = response.data.data || {};
        const next = {};
        CHANNELS.forEach(([key]) => {
          const fee = data[key] || { percent: 0, fixed: 0 };
          next[key] = { percent: String(fee.percent), fixed: String(fee.fixed) };
        });
        setValues(next);
      })
      .catch((err) => {
        if (err.response && err.response.status === 401) onAuthError();
        else {
          setIsError(true);
          setMessage(tx.error);
        }
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (channel, field, value) => {
    setValues((prev) => ({ ...prev, [channel]: { ...prev[channel], [field]: value } }));
  };

  const save = async () => {
    const payload = {};
    for (const [key] of CHANNELS) {
      const percent = parseNumber(values[key].percent);
      const fixed = parseNumber(values[key].fixed);
      if (!Number.isFinite(percent) || !Number.isFinite(fixed) || percent < 0 || percent > 100 || fixed < 0 || fixed > 100) {
        setIsError(true);
        setMessage(tx.error);
        return;
      }
      payload[key] = { percent, fixed };
    }
    setSaving(true);
    setMessage('');
    try {
      await axios.put(`${API_URL}/api/v1/tenant/fees`, payload, headers);
      setIsError(false);
      setMessage(tx.saved);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onAuthError();
        return;
      }
      const detail = err.response && err.response.data && err.response.data.detail;
      setIsError(true);
      setMessage(typeof detail === 'string' ? detail : tx.error);
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'w-24 bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent';

  return (
    <div className="bg-surface border border-edge rounded-2xl p-5 sm:p-6 max-w-lg mb-6">
      <p className="text-white text-sm font-semibold mb-1">{tx.title}</p>
      <p className="text-muted text-xs mb-4">{tx.description}</p>
      {!values ? (
        <p className="text-muted text-sm py-2">{message || tx.loading}</p>
      ) : (
        <>
          <div className="flex flex-col gap-3 mb-4">
            {CHANNELS.map(([key, label]) => (
              <div key={key} className="bg-surfaceAlt border border-edge rounded-lg p-3">
                <p className="text-white text-sm mb-2">{label}</p>
                <div className="flex flex-wrap gap-4">
                  <label className="text-muted text-xs">
                    <span className="block mb-1">{tx.percent}</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={values[key].percent}
                      onChange={(e) => update(key, 'percent', e.target.value)}
                      className={inputClass}
                    />
                  </label>
                  <label className="text-muted text-xs">
                    <span className="block mb-1">{tx.fixed.replace('\u20ac', currency || 'EUR')}</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={values[key].fixed}
                      onChange={(e) => update(key, 'fixed', e.target.value)}
                      className={inputClass}
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>
          {message && <p className={`text-xs mb-3 ${isError ? 'text-red-400' : 'text-accent'}`}>{message}</p>}
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="bg-accent text-panel font-bold text-sm px-5 py-2 rounded-lg cursor-pointer"
          >
            {saving ? tx.saving : tx.save}
          </button>
        </>
      )}
    </div>
  );
}

export default FeeSettingsCard;